import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, X } from 'lucide-react';
import { SNACKBAR_CONFIG } from '../../constants';

interface CopySnackbarProps {
  open: boolean;
  onClose: () => void;
  message?: string;
  instanceKey?: number;
}

/**
 * High-performance, zero-dependency snackbar for copy notifications
 * Replaced heavy @mui/material dependency to optimize bundle size
 */
const CopySnackbar: React.FC<CopySnackbarProps> = ({
  open,
  onClose,
  message = 'Copied to clipboard!',
  instanceKey = 0,
}) => {
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      onClose();
    }, SNACKBAR_CONFIG.AUTO_HIDE_DURATION);

    return () => clearTimeout(timer);
  }, [open, onClose, instanceKey]);

  if (!open || typeof document === 'undefined') {
    return null;
  }

  const content = (
    <div
      key={instanceKey}
      className="app-snackbar"
      style={{
        position: 'fixed',
        top: '24px',
        right: '24px',
        zIndex: 2200,
        display: 'flex',
        alignItems: 'center',
        animation: 'slideInRight 0.22s cubic-bezier(0.16, 1, 0.3, 1)',
      }}
      role="status"
      aria-live="polite"
    >
      <div
        className="app-snackbar__alert"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 16px',
        }}
      >
        <CheckCircle2 size={16} style={{ flexShrink: 0, color: '#a7f3d0' }} />
        <span style={{ flex: 1, whiteSpace: 'nowrap' }}>{message}</span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close notification"
          style={{
            background: 'transparent',
            border: 'none',
            color: 'rgba(248, 250, 252, 0.85)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            padding: '2px',
            marginLeft: '6px',
            borderRadius: '4px',
          }}
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default CopySnackbar;
